import React from "react";
import PropTypes from "prop-types";
import { Box } from "@material-ui/core";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import { formatearFecha } from "src/utils/formatTime";
import { BRAND } from "src/theme/brandTokens";

const FORMATO_FECHA_LARGA = "DD [de] MMMM [de] YYYY HH:mm";

const Eyebrow = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.eyebrow,
      color: BRAND.color.grey50,
      textTransform: "uppercase",
      letterSpacing: BRAND.font.letterSpacing.widest,
      fontWeight: BRAND.font.weight.medium,
      mb: 1,
    }}
  >
    {children}
  </Box>
);

const Valor = ({ children, secundario }) => (
  <Box
    sx={{
      fontSize: secundario ? BRAND.font.size.body : BRAND.font.size.bodyLarge,
      color: secundario ? BRAND.color.grey70 : BRAND.color.inkSoft,
      fontWeight: secundario
        ? BRAND.font.weight.regular
        : BRAND.font.weight.medium,
      lineHeight: BRAND.font.lineHeight.snug,
      mt: secundario ? 0.5 : 0,
    }}
  >
    {children}
  </Box>
);

const Bloque = ({ children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
    {children}
  </Box>
);

const Divisor = () => (
  <Box
    component="hr"
    sx={{
      border: 0,
      borderTop: `1px solid ${BRAND.color.divider}`,
      m: 0,
      my: 4,
    }}
  />
);

/**
 * Render V1 — verificación de QR heredado.
 */
const DetallesV1 = ({ data }) => {
  const d = data.detalles || {};
  const persona = d.personaQueAcepta || {};

  return (
    <Box>
      <Bloque>
        <Box>
          <Eyebrow>Información general</Eyebrow>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 4,
          }}
        >
          <Box>
            <Eyebrow>Solicitante</Eyebrow>
            <Valor>{d.solicitanteRegistrado || "—"}</Valor>
          </Box>
          <Box>
            <Eyebrow>Método de verificación</Eyebrow>
            <Valor>{d.tipoVerificacion || "—"}</Valor>
          </Box>
        </Box>
      </Bloque>

      <Divisor />

      <Bloque>
        <Box>
          <Eyebrow>Aceptada por</Eyebrow>
          <Valor>{persona.nombre || "—"}</Valor>
          {persona.rut && (
            <Valor secundario>
              RUT {formatRut(persona.rut, RutFormat.DOTS_DASH)}
            </Valor>
          )}
          {persona.cargo && <Valor secundario>{persona.cargo}</Valor>}
        </Box>
      </Bloque>

      <Divisor />

      <Bloque>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 4,
          }}
        >
          <Box>
            <Eyebrow>Fecha de aceptación</Eyebrow>
            <Valor>
              {formatearFecha(d.fechaAceptacion, FORMATO_FECHA_LARGA)}
            </Valor>
            {typeof d.diasTranscurridos === "number" && (
              <Valor secundario>
                {d.diasTranscurridos === 0
                  ? "Hoy"
                  : `Hace ${d.diasTranscurridos} día${d.diasTranscurridos === 1 ? "" : "s"}`}
              </Valor>
            )}
          </Box>
          <Box>
            <Eyebrow>Fecha de verificación</Eyebrow>
            <Valor>
              {formatearFecha(d.fechaVerificacion, FORMATO_FECHA_LARGA)}
            </Valor>
          </Box>
        </Box>
      </Bloque>
    </Box>
  );
};

Eyebrow.propTypes = { children: PropTypes.node.isRequired };
Valor.propTypes = {
  children: PropTypes.node.isRequired,
  secundario: PropTypes.bool,
};
Bloque.propTypes = { children: PropTypes.node.isRequired };

DetallesV1.propTypes = {
  data: PropTypes.object.isRequired,
};

export default DetallesV1;
